import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

// ============================================================
// ZUS & Tax Constants — 2026
// Update these each January when new rates are published.
// ============================================================

const ZUS = {
  ulga: { social: 0 },
  preferencyjny: {
    podstawa: 1441.80,
    emerytalna: 0.1952,
    rentowa: 0.08,
    chorobowa: 0.0245,
    wypadkowa: 0.0167,
    fp: 0,
  },
  pelny: {
    podstawa: 5652,
    emerytalna: 0.1952,
    rentowa: 0.08,
    chorobowa: 0.0245,
    wypadkowa: 0.0167,
    fp: 0.0245,
  },
};

const HEALTH = {
  minimum: 432.54,
  ryczalt: [
    { maxRevenue: 60_000, monthly: 498.35 },
    { maxRevenue: 300_000, monthly: 830.58 },
    { maxRevenue: Infinity, monthly: 1495.04 },
  ],
  skalaRate: 0.09,
  liniowyRate: 0.049,
};

const TAX = {
  ryczalt: {
    rate: 0.085,
    rateAbove300k: 0.125,
    healthDeductionPct: 0.5,
  },
  skala: {
    rate1: 0.12,
    rate2: 0.32,
    threshold: 120_000,
    kwotaWolna: 30_000,
  },
  liniowy: {
    rate: 0.19,
    healthDeductionLimit: 14_100,
  },
};

const EMP = {
  socialRate: 0.1371, // 9.76% + 1.5% + 2.45%
  healthRate: 0.09,
  pitRate: 0.12,
  kup: 250,
  monthlyKwota: 300,
};

// ============================================================
// Smart Defaults Table
// ============================================================

const DEFAULTS = {
  home_beginner:         { avgPrice: 100, clientsPerDay: 3, workDays: 22, rent: 300,  productCost: 20, otherCosts: 80 },
  home_intermediate:     { avgPrice: 130, clientsPerDay: 5, workDays: 22, rent: 350,  productCost: 20, otherCosts: 80 },
  home_experienced:      { avgPrice: 150, clientsPerDay: 6, workDays: 24, rent: 400,  productCost: 20, otherCosts: 80 },
  rent_beginner:         { avgPrice: 100, clientsPerDay: 4, workDays: 22, rent: 1200, productCost: 20, otherCosts: 80 },
  rent_intermediate:     { avgPrice: 130, clientsPerDay: 5, workDays: 22, rent: 1200, productCost: 20, otherCosts: 80 },
  rent_experienced:      { avgPrice: 150, clientsPerDay: 5, workDays: 24, rent: 1200, productCost: 20, otherCosts: 80 },
  own_beginner:          { avgPrice: 120, clientsPerDay: 4, workDays: 22, rent: 3800, productCost: 20, otherCosts: 200 },
  own_intermediate:      { avgPrice: 140, clientsPerDay: 5, workDays: 22, rent: 3800, productCost: 20, otherCosts: 200 },
  own_experienced:       { avgPrice: 160, clientsPerDay: 6, workDays: 24, rent: 4300, productCost: 20, otherCosts: 200 },
  employed_beginner:     { salary: 4806, commissionPct: 30, empServiceValue: 120, empServicesMonth: 80 },
  employed_intermediate: { salary: 5000, commissionPct: 35, empServiceValue: 130, empServicesMonth: 100 },
  employed_experienced:  { salary: 5500, commissionPct: 40, empServiceValue: 150, empServicesMonth: 120 },
};

// ============================================================
// Component
// ============================================================

class NailTechCalculator extends Component {
  /** @type {Object} */
  #state = {
    model: 'home',
    experience: 'beginner',
    zus: 'ulga',
    taxForm: 'ryczalt',
    // Self-employed
    avgPrice: 100,
    clientsPerDay: 3,
    workDays: 22,
    rent: 300,
    productCost: 20,
    otherCosts: 80,
    // Employed
    salary: 4806,
    commissionPct: 30,
    empServiceValue: 120,
    empServicesMonth: 80,
  };

  connectedCallback() {
    super.connectedCallback();
    this.#applyDefaults();
    this.#syncInputsFromState();
    this.#toggleSections();
    this.#recalculate();
  }

  // ---- Event handlers (called by declarative on:event="/method") ----

  handleProfileChange() {
    this.#readRadioState();
    this.#applyDefaults();
    this.#syncInputsFromState();
    this.#toggleSections();
    this.#recalculate();
  }

  handleInputChange = debounce(() => {
    this.#readInputState();
    this.#recalculate();
  }, 200);

  // ---- Private: state management ----

  #readRadioState() {
    const model = this.#checkedRadio('radioModel');
    // Experience lives in two radio groups (one per step 2 variant)
    const experience = this.#state.model === 'employed'
      ? this.#checkedRadio('radioExpEmp')
      : this.#checkedRadio('radioExp');
    if (model) this.#state.model = model;
    if (experience) this.#state.experience = experience;
  }

  #checkedRadio(refName) {
    const radios = this.refs[refName];
    if (!Array.isArray(radios)) return null;
    const checked = radios.find((r) => /** @type {HTMLInputElement} */ (r).checked);
    return checked ? /** @type {HTMLInputElement} */ (checked).value : null;
  }

  #readInputState() {
    const s = this.#state;
    const r = this.refs;
    if (s.model === 'employed') {
      s.salary = this.#val(r.salary);
      s.commissionPct = this.#val(r.commissionPct);
      s.empServiceValue = this.#val(r.empServiceValue);
      s.empServicesMonth = this.#val(r.empServicesMonth);
    } else {
      s.avgPrice = this.#val(r.avgPrice);
      s.clientsPerDay = this.#val(r.clientsPerDay);
      s.workDays = this.#val(r.workDays);
      s.rent = this.#val(r.rent);
      s.productCost = this.#val(r.productCost);
      s.otherCosts = this.#val(r.otherCosts);
    }
    s.taxForm = /** @type {HTMLSelectElement} */ (r.taxForm)?.value || 'ryczalt';
    s.zus = /** @type {HTMLSelectElement} */ (r.zusSelect)?.value || 'ulga';
  }

  #val(el) {
    return parseFloat(/** @type {HTMLInputElement} */ (el)?.value) || 0;
  }

  #applyDefaults() {
    const key = `${this.#state.model}_${this.#state.experience}`;
    const defaults = DEFAULTS[key];
    if (defaults) Object.assign(this.#state, defaults);
  }

  #syncInputsFromState() {
    const s = this.#state;
    const r = this.refs;
    // Self-employed inputs
    this.#setInput(r.avgPrice, s.avgPrice);
    this.#setInput(r.clientsPerDay, s.clientsPerDay);
    this.#setInput(r.workDays, s.workDays);
    this.#setInput(r.rent, s.rent);
    this.#setInput(r.productCost, s.productCost);
    this.#setInput(r.otherCosts, s.otherCosts);
    // Employed inputs
    this.#setInput(r.salary, s.salary);
    this.#setInput(r.commissionPct, s.commissionPct);
    this.#setInput(r.empServiceValue, s.empServiceValue);
    this.#setInput(r.empServicesMonth, s.empServicesMonth);
  }

  #setInput(el, value) {
    if (el) /** @type {HTMLInputElement} */ (el).value = String(value);
  }

  #toggleSections() {
    const employed = this.#state.model === 'employed';
    this.refs.selfEmployedInputs?.classList.toggle('ntc__step--hidden', employed);
    this.refs.employedInputs?.classList.toggle('ntc__step--hidden', !employed);
    this.refs.taxSelectorWrap?.classList.toggle('ntc__step--hidden', employed);
    this.refs.selfResults?.classList.toggle('ntc__step--hidden', employed);
    this.refs.empResults?.classList.toggle('ntc__step--hidden', !employed);
  }

  // ---- Calculation dispatch ----

  #recalculate() {
    if (this.#state.model === 'employed') {
      this.#calculateEmployed();
    } else {
      this.#calculateSelfEmployed();
    }
  }

  // ---- Self-employed calculation ----

  #calculateSelfEmployed() {
    const s = this.#state;
    const monthlyRevenue = s.avgPrice * s.clientsPerDay * s.workDays;
    const annualRevenue = monthlyRevenue * 12;
    const productCosts = s.productCost * s.clientsPerDay * s.workDays;
    const fixedCosts = s.rent + s.otherCosts;
    const totalCosts = productCosts + fixedCosts;
    const incomeBeforeTax = monthlyRevenue - totalCosts;
    const annualIncome = incomeBeforeTax * 12;

    const zusSocial = this.#calcZusSocial(s.zus);
    const health = this.#calcHealth(s.taxForm, annualRevenue, annualIncome, zusSocial);
    const tax = this.#calcTax(s.taxForm, annualRevenue, annualIncome, zusSocial, health);
    const net = incomeBeforeTax - zusSocial - health - tax;

    // Update DOM
    const r = this.refs;
    this.#setMoney(r.resultRevenue, monthlyRevenue);
    this.#setMoney(r.resultProductCosts, -productCosts);
    this.#setMoney(r.resultFixedCosts, -fixedCosts);
    this.#setMoney(r.resultIncomeBeforeTax, incomeBeforeTax);
    this.#setMoney(r.resultZusSocial, -zusSocial);
    this.#setMoney(r.resultHealth, -health);
    this.#setMoney(r.resultTax, -tax);
    this.#setMoney(r.resultNet, net);
    this.#setText(r.resultAnnual, `(${this.#formatMoney(net * 12)}/rok)`);

    // Comparison: estimate equivalent employed net
    const empDefaults = DEFAULTS[`employed_${s.experience}`];
    if (empDefaults) {
      const empNet = this.#calcEmployedNet(empDefaults);
      this.#setText(r.resultCompareEmployed, `~${this.#formatMoney(empNet)} netto na etacie`);
    }
  }

  // ---- Employed calculation ----

  #calculateEmployed() {
    const s = this.#state;
    const commission = (s.commissionPct / 100) * s.empServiceValue * s.empServicesMonth;
    const totalBrutto = s.salary + commission;
    const social = totalBrutto * EMP.socialRate;
    const healthBase = totalBrutto - social;
    const health = healthBase * EMP.healthRate;
    const taxBase = Math.max(0, totalBrutto - social - EMP.kup - EMP.monthlyKwota);
    const pit = Math.max(0, taxBase * EMP.pitRate);
    const net = totalBrutto - social - health - pit;

    const r = this.refs;
    this.#setMoney(r.resultEmpSalary, s.salary);
    this.#setMoney(r.resultEmpCommission, commission);
    this.#setMoney(r.resultEmpTotalBrutto, totalBrutto);
    this.#setMoney(r.resultEmpSocial, -social);
    this.#setMoney(r.resultEmpHealth, -health);
    this.#setMoney(r.resultEmpPit, -pit);
    this.#setMoney(r.resultEmpNet, net);

    // Comparison: estimate equivalent self-employed net
    const selfKey = `home_${s.experience}`;
    const selfDefaults = DEFAULTS[selfKey];
    if (selfDefaults) {
      const selfNet = this.#calcSelfEmployedNet(selfDefaults, 'ulga', 'ryczalt');
      this.#setText(r.resultCompareSelf, `~${this.#formatMoney(selfNet)} netto na własnej działalności`);
    }
  }

  /** Quick estimate for employed net from defaults */
  #calcEmployedNet(d) {
    const commission = (d.commissionPct / 100) * d.empServiceValue * d.empServicesMonth;
    const brutto = d.salary + commission;
    const social = brutto * EMP.socialRate;
    const health = (brutto - social) * EMP.healthRate;
    const pit = Math.max(0, (brutto - social - EMP.kup - EMP.monthlyKwota) * EMP.pitRate);
    return brutto - social - health - pit;
  }

  /** Quick estimate for self-employed net from defaults */
  #calcSelfEmployedNet(d, zus, taxForm) {
    const revenue = d.avgPrice * d.clientsPerDay * d.workDays;
    const costs = d.productCost * d.clientsPerDay * d.workDays + d.rent + d.otherCosts;
    const income = revenue - costs;
    const zusSocial = this.#calcZusSocial(zus);
    const health = this.#calcHealth(taxForm, revenue * 12, income * 12, zusSocial);
    const tax = this.#calcTax(taxForm, revenue * 12, income * 12, zusSocial, health);
    return income - zusSocial - health - tax;
  }

  // ---- ZUS Social ----

  #calcZusSocial(zusTier) {
    if (zusTier === 'ulga') return 0;
    const tier = zusTier === 'maly_zus_plus' ? ZUS.preferencyjny : ZUS[zusTier];
    if (!tier || tier.social === 0) return 0;
    const { podstawa, emerytalna, rentowa, chorobowa, wypadkowa, fp } = tier;
    return podstawa * (emerytalna + rentowa + chorobowa + wypadkowa + fp);
  }

  // ---- Health contribution (zdrowotna) ----

  #calcHealth(taxForm, annualRevenue, annualIncome, zusSocialMonthly) {
    if (taxForm === 'ryczalt') {
      for (const tier of HEALTH.ryczalt) {
        if (annualRevenue <= tier.maxRevenue) return tier.monthly;
      }
      return HEALTH.ryczalt[2].monthly;
    }

    const annualIncomeAfterZus = Math.max(0, annualIncome - zusSocialMonthly * 12);
    const monthlyIncomeAfterZus = annualIncomeAfterZus / 12;

    if (taxForm === 'skala') {
      return Math.max(monthlyIncomeAfterZus * HEALTH.skalaRate, HEALTH.minimum);
    }
    // liniowy
    return Math.max(monthlyIncomeAfterZus * HEALTH.liniowyRate, HEALTH.minimum);
  }

  // ---- Tax ----

  #calcTax(taxForm, annualRevenue, annualIncome, zusSocialMonthly, healthMonthly) {
    const annualZus = zusSocialMonthly * 12;
    const annualHealth = healthMonthly * 12;

    if (taxForm === 'ryczalt') {
      const healthDeduction = annualHealth * TAX.ryczalt.healthDeductionPct;
      let annualTax;
      if (annualRevenue <= 300_000) {
        annualTax = Math.max(0, annualRevenue - healthDeduction) * TAX.ryczalt.rate;
      } else {
        const base300k = Math.max(0, 300_000 - healthDeduction);
        annualTax = base300k * TAX.ryczalt.rate + (annualRevenue - 300_000) * TAX.ryczalt.rateAbove300k;
      }
      return Math.max(0, annualTax / 12);
    }

    if (taxForm === 'skala') {
      const taxableAnnual = Math.max(0, annualIncome - annualZus);
      const afterKwota = taxableAnnual - TAX.skala.kwotaWolna;
      let annualTax;
      if (afterKwota <= 0) {
        annualTax = 0;
      } else if (afterKwota <= TAX.skala.threshold) {
        annualTax = afterKwota * TAX.skala.rate1;
      } else {
        annualTax = TAX.skala.threshold * TAX.skala.rate1 + (afterKwota - TAX.skala.threshold) * TAX.skala.rate2;
      }
      return Math.max(0, annualTax / 12);
    }

    // liniowy
    const healthDeduction = Math.min(annualHealth, TAX.liniowy.healthDeductionLimit);
    const taxableAnnual = Math.max(0, annualIncome - annualZus - healthDeduction);
    return Math.max(0, (taxableAnnual * TAX.liniowy.rate) / 12);
  }

  // ---- DOM helpers ----

  #setMoney(el, value) {
    if (el) el.textContent = this.#formatMoney(value);
  }

  #setText(el, text) {
    if (el) el.textContent = text;
  }

  #formatMoney(value) {
    const rounded = Math.round(value);
    const abs = Math.abs(rounded);
    const formatted = abs.toLocaleString('pl-PL');
    if (rounded < 0) return `−${formatted} zł`;
    return `${formatted} zł`;
  }
}

if (!customElements.get('nail-tech-calculator')) {
  customElements.define('nail-tech-calculator', NailTechCalculator);
}
